import { useData } from "@hooks/useData";
import { ID } from "@datassential/platform-ui-lib";
import { components } from "@apiSchema";
import { ISocialPost, SocialPosts } from "@models/SocialPosts";
import { ChartValue, SocialLineChartColumn } from "@components/SocialLineChart/SocialLineChart";
import http from "@core/fetch";
import apiConfig from "@apiConfig";
import { iPermissionFields, PermissionLevelEnum, PermissionSection } from "@models/PermissionSection";
import dayjs from "dayjs";
import { v4 as uuid } from "uuid";

type Response = components['schemas']['SocialApiKeywordProfileResponse'];
type ResponseWrapper = components['schemas']['SocialApiKeywordProfileResponseChainPermissionWrapper'];
type Aggregation = components['schemas']['SocialApiKeywordAggregation'];

export interface ChainSocialChartData {
    mentions: ChartValue[];
    engagement: ChartValue[];
}

export interface ChainSocialGrowth {
    past12Month: number | null;
    past1Month: number | null;
}

export interface ChainSocialData extends iPermissionFields {
    chartData: ChainSocialChartData;
    columns: SocialLineChartColumn[];
    growth: {
        mentions: ChainSocialGrowth;
        engagement: ChainSocialGrowth;
    };
    posts: ISocialPost[];
}

const EMPTY_GROWTH: ChainSocialGrowth = { past12Month: null, past1Month: null };

export const DEFAULT_CHAIN_SOCIAL_DATA: ChainSocialData = {
    hasData: false,
    permissionLevel: PermissionLevelEnum.None,
    chartData: { mentions: [], engagement: [] },
    columns: [],
    growth: {
        mentions: EMPTY_GROWTH,
        engagement: EMPTY_GROWTH,
    },
    posts: [],
};

function generateFakeChartValues(count: number): ChartValue[] {
    return Array.from({ length: count }, () => Math.floor(Math.random() * 1500));
}

function generateFakeColumns(): SocialLineChartColumn[] {
    const today = dayjs();
    return Array.from({ length: 12 }, (_, i) => {
        const month = today.clone().add(i - 11, 'month');
        return {
            id: month.format('YYYY-MM') as ID,
            title: month.format('MMM'),
        };
    });
}

export const FAKE_CHAIN_SOCIAL_DATA: ChainSocialData = {
    hasData: true,
    permissionLevel: PermissionLevelEnum.Limited,
    chartData: {
        mentions: generateFakeChartValues(12),
        engagement: generateFakeChartValues(12),
    },
    columns: generateFakeColumns(),
    growth: {
        mentions: { past12Month: 12, past1Month: 5 },
        engagement: { past12Month: 8, past1Month: 3 },
    },
    posts: SocialPosts.fakeData.regular,
};

const REQUEST_TYPE: keyof typeof apiConfig = 'get social data by keyword id';
const KEYWORD_TYPE_CHAIN_PROFILE = 3;

function findAggregation(aggregations: Aggregation[] | null | undefined, type: string): ChainSocialGrowth {
    const agg = (aggregations ?? []).find(a => (a.type ?? '').toLowerCase() === type.toLowerCase());
    if (!agg) return EMPTY_GROWTH;
    return {
        past12Month: agg.twelveMonthGrowth ?? null,
        past1Month: agg.thirtyDayGrowth ?? null,
    };
}

function mapPosts(posts: Response['posts']): ISocialPost[] {
    return (posts ?? []).map(p => ({
            id: uuid(),
            source: SocialPosts.getPostSource(p.type ?? ''),
            sentiment: p.sentiment ?? 'neutral',
            text: p.content ?? '',
            date: p.date ?? '',
            engagement: p.engagementCount ?? 0,
            url: p.url ?? '',
        }));
}

async function getChainSocialData(chainId: ID | null): Promise<ChainSocialData> {
    if (chainId == null) return DEFAULT_CHAIN_SOCIAL_DATA;

    try {
        const wrapper: ResponseWrapper = await http.get(REQUEST_TYPE, {
            id: chainId,
            keywordTypeId: KEYWORD_TYPE_CHAIN_PROFILE,
            isSocial: false,
            isHistorical: false,
        });

        const response = wrapper?.data;
        const permissionLevel = PermissionSection.getPermissionLevel(wrapper?.permissionLevel);
        const hasData = wrapper?.hasData ?? false;

        // If user doesn't have full access and data is null, show fake data to entice trial request
        if (permissionLevel !== PermissionLevelEnum.Full && !response && hasData) {
            return { ...FAKE_CHAIN_SOCIAL_DATA, permissionLevel };
        }

        const mentionsList = response?.mentions ?? [];
        const columns: SocialLineChartColumn[] = mentionsList.map(m => ({
            id: (m.monthStartDate ?? '') as ID,
            title: m.monthStartDate ? dayjs(m.monthStartDate).format('MMM') : '',
        }));

        const mentionsValues: ChartValue[] = mentionsList.map(m => m.count ?? null);
        const engagementValues: ChartValue[] = mentionsList.map(m => m.engagementCount ?? null);

        const posts = mapPosts(response?.posts);

        return {
            hasData,
            permissionLevel,
            chartData: {
                mentions: mentionsValues,
                engagement: engagementValues,
            },
            columns,
            growth: {
                mentions: findAggregation(response?.aggregations, 'mentions'),
                engagement: findAggregation(response?.aggregations, 'reach'),
            },
            posts,
        };
    } catch (err) {
        console.error('Fetch error:', REQUEST_TYPE, err);
        return DEFAULT_CHAIN_SOCIAL_DATA;
    }
}

export function useChainSocialData(chainId: ID | null) {
    return useData(DEFAULT_CHAIN_SOCIAL_DATA, getChainSocialData, chainId);
}
